/**
 * Pairing code helpers (PLAYER_PROTOCOL.md §7.3): 6 decimal digits, displayed `482 731`.
 * The QR payload is `https://app.signetix.io/pair?c=<code>&k=<nonce>`.
 */

export const CODE_LENGTH = 6

/** Keeps digits only, capped at 6. */
export function normalizeCode(input: string | null | undefined): string {
  return (input ?? '').replace(/\D+/g, '').slice(0, CODE_LENGTH)
}

/** `482731` → `482 731`; partial input is grouped as typed (`4827` → `482 7`). */
export function formatCode(input: string | null | undefined): string {
  const digits = normalizeCode(input)
  return digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits
}

export function isCompleteCode(input: string | null | undefined): boolean {
  return /^\d{6}$/.test(input ?? '')
}

/** base64url, 16–64 chars (the protocol uses 22). Anything else is ignored. */
export function normalizeNonce(input: string | null | undefined): string | null {
  const value = (input ?? '').trim()
  return /^[A-Za-z0-9_-]{16,64}$/.test(value) ? value : null
}

/**
 * Parses whatever the user typed or pasted: a code (`482 731`, `482-731`), or a full QR URL
 * (`https://app.signetix.io/pair?c=482731&k=…`) which also yields the claim nonce.
 */
export function parsePairingInput(text: string | null | undefined): { code: string; nonce: string | null } {
  const raw = (text ?? '').trim()
  if (/[?&]c=/.test(raw) || /^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw, 'https://app.signetix.io')
      const c = url.searchParams.get('c')
      if (c !== null) return { code: normalizeCode(c), nonce: normalizeNonce(url.searchParams.get('k')) }
    } catch {
      // fall through to plain digits
    }
  }
  return { code: normalizeCode(raw), nonce: null }
}
